import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @returns {ScrollBehavior} */
const scrollBehavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

/**
 * One-time reveal for elements with [data-lux-reveal]: items marked [data-lux-reveal-item]
 * fade and rise in when the section enters the viewport (styles in base.css).
 * Sections already on screen, reduced motion and the theme editor skip the animation.
 * @param {HTMLElement} element
 * @returns {IntersectionObserver | null} The observer, so the caller can disconnect it
 */
function setUpLuxReveal(element) {
  if (!element.hasAttribute('data-lux-reveal') || reducedMotion.matches || window.Shopify?.designMode) return null;
  if (element.getBoundingClientRect().top < window.innerHeight) return null;

  element.dataset.luxReveal = 'pending';
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      element.dataset.luxReveal = 'visible';
      observer.disconnect();
    },
    { rootMargin: '0px 0px -15% 0px' }
  );
  observer.observe(element);
  return observer;
}

/**
 * Updates [data-can-previous] / [data-can-next] on a carousel from its track's scroll position.
 * @param {HTMLElement} track
 */
function updateCarouselArrows(track) {
  const carousel = track.parentElement;
  if (!carousel) return;
  const maxScroll = track.scrollWidth - track.clientWidth;
  const position = Math.abs(track.scrollLeft);
  carousel.toggleAttribute('data-can-previous', position > 2);
  carousel.toggleAttribute('data-can-next', position < maxScroll - 2);
}

/**
 * Scrolls a carousel track by one page of fully visible items.
 * @param {HTMLElement} track
 * @param {1 | -1} direction
 */
function scrollCarouselPage(track, direction) {
  const item = track.firstElementChild;
  if (!(item instanceof HTMLElement)) return;

  const styles = getComputedStyle(track);
  const gap = parseFloat(styles.columnGap) || 0;
  const padding = (parseFloat(styles.paddingInlineStart) || 0) + (parseFloat(styles.paddingInlineEnd) || 0);
  const step = item.offsetWidth + gap;
  const perPage = Math.max(1, Math.floor((track.clientWidth - padding + gap) / step));
  const sign = styles.direction === 'rtl' ? -1 : 1;

  track.scrollBy({ left: direction * sign * step * perPage, behavior: scrollBehavior() });
}

/**
 * Lazy images inside a hidden tab panel only start loading once the panel is shown.
 * Load them when the section gets close to the viewport so switching tabs is instant.
 * @param {HTMLElement} element - Section root containing .lux-tabs__panel elements
 * @returns {IntersectionObserver}
 */
function preloadHiddenPanels(element) {
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      for (const image of element.querySelectorAll('.lux-tabs__panel[hidden] img[loading="lazy"]')) {
        if (image instanceof HTMLImageElement && !image.classList.contains('lux-card__image--secondary')) {
          image.loading = 'eager';
        }
      }
      observer.disconnect();
    },
    { rootMargin: '200px 0px' }
  );
  observer.observe(element);
  return observer;
}

/**
 * @typedef {Object} LuxTabsRefs
 * @property {HTMLButtonElement[]} [tabs] - Tab buttons
 * @property {HTMLElement} [indicator] - Sliding underline under the selected tab
 */

/**
 * Lux home tabs (snippets/lux-tabs.liquid): switches which panel is visible (panels are found by
 * each tab's aria-controls), slides the underline and supports arrow, Home and End keys.
 * Dispatches a bubbling "lux-tabs:change" event so the section can react.
 *
 * @extends {Component<LuxTabsRefs>}
 */
class LuxTabs extends Component {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver = new ResizeObserver(() => this.#moveIndicator());
    this.#resizeObserver.observe(this);
    this.#moveIndicator();
    // Enable the underline transition only after its first position is set
    requestAnimationFrame(() => {
      this.dataset.ready = '';
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /** @param {Event} event */
  selectTab(event) {
    const tab = event.target;
    if (tab instanceof HTMLButtonElement) this.#activate(tab);
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    const current = tabs.findIndex((tab) => tab === event.target);
    if (current === -1) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';

    let next = current;
    if (event.key === forward) next = (current + 1) % tabs.length;
    else if (event.key === backward) next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    const tab = tabs[next];
    if (!tab) return;
    tab.focus();
    this.#activate(tab);
  }

  /** @param {HTMLButtonElement} selectedTab */
  #activate(selectedTab) {
    /** @type {HTMLElement | null} */
    let selectedPanel = null;

    for (const tab of this.refs.tabs ?? []) {
      const selected = tab === selectedTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;

      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (panel) {
        panel.hidden = !selected;
        if (selected) selectedPanel = panel;
      }
    }

    this.#moveIndicator();
    this.dispatchEvent(new CustomEvent('lux-tabs:change', { bubbles: true, detail: { panel: selectedPanel } }));
  }

  #moveIndicator() {
    const { indicator, tabs = [] } = this.refs;
    const selected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (!indicator || !selected) return;

    indicator.style.setProperty('--lux-tabs-indicator-x', `${selected.offsetLeft}px`);
    indicator.style.setProperty('--lux-tabs-indicator-width', String(selected.offsetWidth));
  }
}

/**
 * Section root that only needs the one-time Lux reveal (e.g. Colecciones Lux).
 */
class LuxReveal extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  /** @type {IntersectionObserver | null} */
  #preloadObserver = null;

  connectedCallback() {
    this.#revealObserver = setUpLuxReveal(this);
    if (this.querySelector('.lux-tabs__panel[hidden]')) this.#preloadObserver = preloadHiddenPanels(this);
  }

  disconnectedCallback() {
    this.#revealObserver?.disconnect();
    this.#preloadObserver?.disconnect();
  }
}

/**
 * @typedef {Object} LuxHeroRefs
 * @property {HTMLElement[]} [slides] - Background slides
 * @property {HTMLButtonElement[]} [dots] - Slide indicators
 * @property {HTMLElement} [media] - Slides container, used for swipe
 */

/** Matches the crossfade duration in hero-lux.liquid */
const HERO_CROSSFADE_MS = 1200;
const SWIPE_THRESHOLD = 50;

/**
 * Full-screen hero. With several slides it crossfades between them on a timer driven by the
 * active dot's progress-ring animation, so pausing the animation also pauses the timer.
 * Its round arrow scrolls to the section that follows it.
 *
 * @extends {Component<LuxHeroRefs>}
 */
class LuxHero extends Component {
  #current = 0;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #previousTimeout = null;

  /** @type {Set<string>} */
  #pauseReasons = new Set();

  #touchStartX = 0;
  #touchStartY = 0;

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('data-slideshow')) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (!reducedMotion.matches) this.dataset.autoplay = '';

    this.addEventListener(
      'animationend',
      (event) => {
        if (event.animationName === 'hero-lux-progress') this.#goTo(this.#current + 1);
      },
      { signal }
    );

    document.addEventListener('visibilitychange', () => this.#setPaused('hidden', document.hidden), { signal });

    const { media } = this.refs;
    media?.addEventListener('touchstart', this.#handleTouchStart, { passive: true, signal });
    media?.addEventListener('touchend', this.#handleTouchEnd, { passive: true, signal });

    // Theme editor: show the selected slide and hold it while it is being edited
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const index = (this.refs.slides ?? []).indexOf(/** @type {HTMLElement} */ (event.target));
        if (index === -1) return;
        this.#goTo(index);
        this.#setPaused('editor', true);
      },
      { signal }
    );
    document.addEventListener('shopify:block:deselect', () => this.#setPaused('editor', false), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    if (this.#previousTimeout) clearTimeout(this.#previousTimeout);
  }

  /** @param {Event} event */
  selectSlide(event) {
    const index = (this.refs.dots ?? []).indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (index !== -1) this.#goTo(index);
  }

  scrollToNext() {
    const next = this.closest('.shopify-section')?.nextElementSibling;
    if (!(next instanceof HTMLElement)) return;

    // Leave room for the sticky header that covers the top of the viewport
    const header = document.querySelector('header-component[sticky]');
    const headerHeight = header ? Math.round(header.getBoundingClientRect().height) : 0;
    next.style.scrollMarginTop = `${headerHeight}px`;
    next.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
  }

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    const touch = event.changedTouches[0];
    if (!touch) return;
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    if (!touch) return;
    const deltaX = touch.clientX - this.#touchStartX;
    const deltaY = touch.clientY - this.#touchStartY;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? deltaX > 0 : deltaX < 0;
    this.#goTo(this.#current + (forward ? 1 : -1));
  };

  /**
   * @param {string} reason
   * @param {boolean} paused
   */
  #setPaused(reason, paused) {
    if (paused) this.#pauseReasons.add(reason);
    else this.#pauseReasons.delete(reason);
    this.toggleAttribute('data-paused', this.#pauseReasons.size > 0);
  }

  /** @param {number} index */
  #goTo(index) {
    const slides = this.refs.slides ?? [];
    const dots = this.refs.dots ?? [];
    if (slides.length < 2) return;

    const nextIndex = (index + slides.length) % slides.length;
    if (nextIndex === this.#current) return;

    const outgoing = slides[this.#current];
    const incoming = slides[nextIndex];
    if (!outgoing || !incoming) return;

    // Settle any crossfade still in progress
    if (this.#previousTimeout) clearTimeout(this.#previousTimeout);
    for (const slide of slides) {
      if (slide.dataset.state === 'previous') slide.dataset.state = '';
    }

    for (const image of incoming.querySelectorAll('img[loading="lazy"]')) {
      if (image instanceof HTMLImageElement) image.loading = 'eager';
    }

    outgoing.dataset.state = 'previous';
    outgoing.setAttribute('aria-hidden', 'true');
    incoming.dataset.state = 'active';
    incoming.removeAttribute('aria-hidden');

    dots[this.#current]?.removeAttribute('aria-current');
    dots[nextIndex]?.setAttribute('aria-current', 'true');

    this.#current = nextIndex;
    this.#previousTimeout = window.setTimeout(() => {
      if (outgoing.dataset.state === 'previous') outgoing.dataset.state = '';
    }, HERO_CROSSFADE_MS);
  }
}

/**
 * Tabbed best sellers. Both product lists are rendered in Liquid and switched by <lux-tabs>;
 * this drives the carousel arrows and reveals the section when it scrolls into view.
 */
class LuxBestSellers extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  /** @type {IntersectionObserver | null} */
  #preloadObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    for (const track of this.#tracks) {
      track.addEventListener('scroll', () => this.#updateCarousel(track), { passive: true, signal });
    }

    // A newly shown panel needs its arrows measured
    this.addEventListener('lux-tabs:change', () => this.#update(), { signal });

    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this);

    this.#update();
    this.#setUpReveal();
    this.#preloadObserver = preloadHiddenPanels(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#revealObserver?.disconnect();
    this.#preloadObserver?.disconnect();
  }

  /** @param {Event} event */
  scrollPrevious(event) {
    this.#scrollCarousel(event, -1);
  }

  /** @param {Event} event */
  scrollNext(event) {
    this.#scrollCarousel(event, 1);
  }

  get #tracks() {
    return /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.lux-bs__track'));
  }

  #update() {
    for (const track of this.#tracks) this.#updateCarousel(track);
  }

  /** @param {HTMLElement} track */
  #updateCarousel(track) {
    const carousel = track.parentElement;
    if (!carousel || track.offsetParent === null) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    carousel.toggleAttribute('data-can-previous', position > 2);
    carousel.toggleAttribute('data-can-next', position < maxScroll - 2);

    // Center the arrows on the product images rather than the whole card
    const media = track.querySelector('.lux-card__media');
    if (media) {
      const mediaRect = media.getBoundingClientRect();
      const top = mediaRect.top - carousel.getBoundingClientRect().top + mediaRect.height / 2;
      carousel.style.setProperty('--lux-bs-arrow-top', `${Math.round(top)}px`);
    }
  }

  /**
   * Scrolls the carousel by one page of fully visible slides.
   * @param {Event} event
   * @param {1 | -1} direction
   */
  #scrollCarousel(event, direction) {
    if (!(event.target instanceof Element)) return;
    const track = event.target.closest('.lux-bs__carousel')?.querySelector('.lux-bs__track');
    if (track instanceof HTMLElement) scrollCarouselPage(track, direction);
  }

  #setUpReveal() {
    if (!this.hasAttribute('data-reveal') || reducedMotion.matches || window.Shopify?.designMode) return;
    // Sections that are already on screen stay visible
    if (this.getBoundingClientRect().top < window.innerHeight) return;

    this.dataset.reveal = 'pending';
    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.dataset.reveal = 'visible';
        this.#revealObserver?.disconnect();
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    this.#revealObserver.observe(this);
  }
}

/**
 * @typedef {Object} LuxReviewsRefs
 * @property {HTMLElement} [viewport] - Visible strip
 * @property {HTMLElement} [track] - The row (two identical copies) that the CSS animation moves
 * @property {HTMLDialogElement} [lightbox] - Enlarged screenshot dialog
 * @property {HTMLElement} [lightboxBox] - Box around the enlarged image
 * @property {HTMLImageElement} [lightboxImage] - Enlarged image
 */

/**
 * Infinite reviews marquee. CSS moves the doubled row by -50%; this sets the duration from the
 * row width so the speed is constant in px/s, runs it only while on screen, pauses it while a
 * finger holds it or the lightbox is open, and opens screenshots in a lightbox.
 *
 * @extends {Component<LuxReviewsRefs>}
 */
class LuxReviews extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #viewObserver = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  /** @type {Set<string>} */
  #pauseReasons = new Set();

  /** @type {HTMLElement | null} */
  #opener = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { track, viewport, lightbox } = this.refs;

    if (track) {
      this.#setDuration();
      this.#resizeObserver = new ResizeObserver(() => this.#setDuration());
      this.#resizeObserver.observe(track);
    }

    this.#viewObserver = new IntersectionObserver(([entry]) => {
      this.toggleAttribute('data-in-view', Boolean(entry?.isIntersecting));
    });
    this.#viewObserver.observe(this);

    // Touch: hold to pause
    viewport?.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse') this.#setPaused('touch', true);
      },
      { signal }
    );
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) {
      viewport?.addEventListener(type, () => this.#setPaused('touch', false), { signal });
    }

    lightbox?.addEventListener('close', () => this.#cleanUpLightbox(), { signal });
    this.#revealObserver = setUpLuxReveal(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#viewObserver?.disconnect();
    this.#revealObserver?.disconnect();
    if (this.refs.lightbox?.open) this.refs.lightbox.close();
  }

  /** @param {Event} event */
  openLightbox(event) {
    const button = event.target;
    const { lightbox, lightboxImage } = this.refs;
    if (!(button instanceof HTMLElement) || !lightbox || !lightboxImage) return;

    const src = button.dataset.fullSrc;
    if (!src) return;
    lightboxImage.src = src;
    lightboxImage.alt = button.dataset.alt || '';

    this.#opener = button;
    this.#setPaused('lightbox', true);
    lightbox.showModal();
    lockScroll(lightbox);
  }

  closeLightbox() {
    this.refs.lightbox?.close();
  }

  /** @param {MouseEvent} event */
  handleLightboxClick(event) {
    const box = this.refs.lightboxBox;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) this.closeLightbox();
  }

  #cleanUpLightbox() {
    const { lightbox, lightboxImage } = this.refs;
    if (lightbox) unlockScroll(lightbox);
    lightboxImage?.removeAttribute('src');
    this.#setPaused('lightbox', false);
    this.#opener?.focus({ preventScroll: true });
    this.#opener = null;
  }

  /** Keeps the scroll speed constant (px per second) whatever the number of reviews. */
  #setDuration() {
    const { track } = this.refs;
    if (!track) return;
    const speed = Number(this.dataset.speed) || 30;
    const distance = track.scrollWidth / 2;
    if (distance > 0) this.style.setProperty('--reviews-lux-duration', `${Math.round(distance / speed)}s`);
  }

  /**
   * @param {string} reason
   * @param {boolean} paused
   */
  #setPaused(reason, paused) {
    if (paused) this.#pauseReasons.add(reason);
    else this.#pauseReasons.delete(reason);
    this.toggleAttribute('data-paused', this.#pauseReasons.size > 0);
  }
}

/**
 * @typedef {Object} LuxBenefitsRefs
 * @property {HTMLElement} [viewport] - Benefits grid
 * @property {HTMLElement[]} [items] - Benefit columns
 * @property {HTMLButtonElement[]} [dots] - Mobile indicators
 */

const mobileQuery = window.matchMedia('(max-width: 749px)');

/**
 * Benefits row. On mobile it shows one benefit at a time and rotates them on a timer while the
 * section is on screen (no autoplay with reduced motion); dots and swipe change it by hand.
 *
 * @extends {Component<LuxBenefitsRefs>}
 */
class LuxBenefits extends Component {
  #current = 0;
  #inView = false;
  #touchStartX = 0;
  #touchStartY = 0;

  /** @type {number | null} */
  #timer = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {IntersectionObserver | null} */
  #viewObserver = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#viewObserver = new IntersectionObserver(([entry]) => {
      this.#inView = Boolean(entry?.isIntersecting);
      this.#restartTimer();
    });
    this.#viewObserver.observe(this);

    document.addEventListener('visibilitychange', () => this.#restartTimer(), { signal });
    mobileQuery.addEventListener('change', () => this.#restartTimer(), { signal });

    const { viewport } = this.refs;
    viewport?.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        this.#touchStartX = touch.clientX;
        this.#touchStartY = touch.clientY;
      },
      { passive: true, signal }
    );
    viewport?.addEventListener(
      'touchend',
      (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        const deltaX = touch.clientX - this.#touchStartX;
        const deltaY = touch.clientY - this.#touchStartY;
        if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY)) return;
        this.#select(this.#current + (deltaX < 0 ? 1 : -1));
        this.#restartTimer();
      },
      { passive: true, signal }
    );

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const index = (this.refs.items ?? []).indexOf(/** @type {HTMLElement} */ (event.target));
        if (index !== -1) this.#select(index);
      },
      { signal }
    );

    this.#revealObserver = setUpLuxReveal(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#viewObserver?.disconnect();
    this.#revealObserver?.disconnect();
    this.#stopTimer();
  }

  /** @param {Event} event */
  selectBenefit(event) {
    const index = (this.refs.dots ?? []).indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (index === -1) return;
    this.#select(index);
    this.#restartTimer();
  }

  /** @param {number} index */
  #select(index) {
    const items = this.refs.items ?? [];
    if (items.length < 2) return;
    const next = (index + items.length) % items.length;

    items.forEach((item, i) => item.toggleAttribute('data-active', i === next));
    (this.refs.dots ?? []).forEach((dot, i) => {
      if (i === next) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    this.#current = next;
  }

  #restartTimer() {
    this.#stopTimer();
    const canAutoplay =
      this.#inView && mobileQuery.matches && !reducedMotion.matches && !document.hidden && (this.refs.items?.length ?? 0) > 1;
    if (!canAutoplay) return;

    const seconds = Number(this.dataset.interval) || 5;
    this.#timer = window.setInterval(() => this.#select(this.#current + 1), seconds * 1000);
  }

  #stopTimer() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
  }
}

/**
 * @typedef {Object} LuxCollectionHeroRefs
 * @property {HTMLElement} [content] - Subtitle, title and description
 */

/**
 * Collection hero. The photo is fixed in CSS; this only fades the text as it scrolls away,
 * with one rAF-throttled scroll listener that runs while the hero is on screen.
 *
 * @extends {Component<LuxCollectionHeroRefs>}
 */
class LuxCollectionHero extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {IntersectionObserver | null} */
  #viewObserver = null;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #frame = null;

  connectedCallback() {
    super.connectedCallback();
    if (reducedMotion.matches || this.classList.contains('collection-hero-lux--plain')) return;

    this.#abortController = new AbortController();
    scrollContainerMediaQuery.addEventListener('change', () => this.#bindScroll(), {
      signal: this.#abortController.signal,
    });

    this.#viewObserver = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) this.#bindScroll();
      else this.#unbindScroll();
    });
    this.#viewObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#viewObserver?.disconnect();
    this.#unbindScroll();
  }

  #bindScroll() {
    this.#unbindScroll();
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#update();
  }

  #unbindScroll() {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = null;
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update();
    });
  };

  #update() {
    const { content } = this.refs;
    if (!content) return;
    const rect = this.getBoundingClientRect();
    const progress = Math.min(Math.max(-rect.top / (rect.height * 0.6), 0), 1);
    content.style.opacity = String(1 - progress);
  }
}

const COLUMNS_STORAGE_KEY = 'lux-collection-columns';

/**
 * @typedef {Object} LuxCollectionRefs
 * @property {HTMLElement} [results] - Grid wrapper, dimmed while results load
 * @property {HTMLElement} [grid] - Product grid
 * @property {HTMLElement} [more] - "Load more" wrapper
 * @property {HTMLDetailsElement} [sort] - Sort dropdown
 * @property {HTMLDialogElement} [drawer] - Filter drawer
 * @property {HTMLFormElement} [filterForm] - Filter form
 */

/**
 * Collection listing. Filtering, sorting and chips update the URL and re-render this section with
 * Horizon's sectionRenderer (no page reload); "load more" appends the next page from the same
 * API. Also keeps the chosen grid columns in localStorage and reveals cards row by row.
 *
 * @extends {Component<LuxCollectionRefs>}
 */
class LuxCollection extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {IntersectionObserver | null} */
  #cardObserver = null;

  #firstBatch = true;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    window.addEventListener('popstate', () => this.#render(), { signal });

    document.addEventListener(
      'click',
      (event) => {
        const { sort } = this.refs;
        if (sort?.open && event.target instanceof Node && !sort.contains(event.target)) sort.open = false;
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.refs.sort?.open) {
          this.refs.sort.open = false;
          this.refs.sort.querySelector('summary')?.focus();
        }
      },
      { signal }
    );

    this.#refresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#cardObserver?.disconnect();
    if (this.refs.drawer?.open) this.refs.drawer.close();
  }

  get sectionId() {
    return this.dataset.sectionId ?? '';
  }

  /** @param {Event} event */
  setColumns(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const device = button.dataset.device === 'mobile' ? 'mobile' : 'desktop';
    const columns = button.dataset.columns ?? '';

    const saved = this.#readColumns();
    saved[device] = columns;
    try {
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(saved));
    } catch {
      // Storage can be unavailable (private mode); the choice still applies to this page
    }
    this.#applyColumns();
  }

  /** @param {Event} event */
  selectSort(event) {
    event.preventDefault();
    const link = event.target;
    if (!(link instanceof HTMLElement) || !link.dataset.sort) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', link.dataset.sort);
    url.searchParams.delete('page');
    if (this.refs.sort) this.refs.sort.open = false;
    this.#navigate(url);
  }

  openFilters() {
    const { drawer } = this.refs;
    if (!drawer || drawer.open) return;
    drawer.addEventListener('close', () => unlockScroll(drawer), { once: true });
    drawer.showModal();
    lockScroll(drawer);
  }

  closeFilters() {
    this.refs.drawer?.close();
  }

  /** @param {MouseEvent} event */
  handleDrawerClick(event) {
    const { drawer } = this.refs;
    if (!drawer) return;
    const rect = drawer.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) this.closeFilters();
  }

  /** @param {SubmitEvent} event */
  applyFilters(event) {
    event.preventDefault();
    const { filterForm } = this.refs;
    if (!filterForm) return;

    const params = new URLSearchParams();
    for (const [name, value] of new FormData(filterForm).entries()) {
      if (typeof value === 'string' && value.trim() !== '') params.append(name, value);
    }

    const url = new URL(filterForm.action, window.location.origin);
    url.search = params.toString();
    this.#navigate(url);
  }

  /** @param {Event} event */
  navigateLink(event) {
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    this.#navigate(new URL(link.href));
  }

  /** @param {Event} event */
  async loadMore(event) {
    const link = event.target;
    const { grid, more } = this.refs;
    if (!(link instanceof HTMLAnchorElement) || !grid || !more) return;
    event.preventDefault();

    more.dataset.loading = '';
    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(link.href));
      const page = new DOMParser().parseFromString(html, 'text/html');
      grid.append(...page.querySelectorAll('[data-collection-item]'));

      const nextLink = page.querySelector('.collection-lux__more a');
      if (nextLink instanceof HTMLAnchorElement) {
        link.href = nextLink.href;
        delete more.dataset.loading;
      } else {
        more.remove();
      }
      this.#applyColumns();
      this.#observeCards();
    } catch {
      window.location.href = link.href;
    }
  }

  /**
   * @param {URL} url
   */
  async #navigate(url) {
    if (this.refs.drawer?.open) this.refs.drawer.close();
    history.pushState({ luxCollection: true }, '', url.toString());
    await this.#render();

    // When the new results start above the viewport, bring the start of the grid back into view
    // below the sticky header (the only element covering the top of the page)
    const target = this.refs.grid ?? this.refs.results;
    if (target && target.getBoundingClientRect().top < 0) {
      const header = document.querySelector('header-component');
      target.style.scrollMarginTop = `${header ? Math.round(header.getBoundingClientRect().height) : 0}px`;
      target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    }
  }

  async #render() {
    const { results } = this.refs;
    if (results) results.dataset.loading = '';
    try {
      await sectionRenderer.renderSection(this.sectionId);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      window.location.reload();
      return;
    }
    // morph may keep this element; make sure the new DOM is wired up
    this.#refresh();
  }

  #refresh() {
    delete this.refs.results?.dataset.loading;
    this.#applyColumns();
    this.#observeCards();
  }

  /** @returns {{ desktop?: string, mobile?: string }} */
  #readColumns() {
    try {
      const value = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY) || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch {
      return {};
    }
  }

  #applyColumns() {
    const saved = this.#readColumns();
    if (saved.desktop === '3' || saved.desktop === '4') this.dataset.columnsDesktop = saved.desktop;
    if (saved.mobile === '1' || saved.mobile === '2') this.dataset.columnsMobile = saved.mobile;

    const desktop = Number(this.dataset.columnsDesktop) || 4;
    const mobile = Number(this.dataset.columnsMobile) || 2;
    const tablet = Math.min(desktop, 3);

    for (const button of this.querySelectorAll('.collection-lux__view')) {
      if (!(button instanceof HTMLElement)) continue;
      const current = button.dataset.device === 'mobile' ? mobile : desktop;
      button.setAttribute('aria-pressed', String(Number(button.dataset.columns) === current));
    }

    // Keep image sizes in step with the columns so the browser picks the right width
    const sizes =
      `(min-width: 990px) calc((100vw - ${128 + (desktop - 1) * 48}px) / ${desktop}), ` +
      `(min-width: 750px) calc((100vw - ${64 + (tablet - 1) * 32}px) / ${tablet}), ` +
      `calc((100vw - ${32 + (mobile - 1) * 16}px) / ${mobile})`;
    for (const image of this.querySelectorAll('.collection-lux__grid .lux-card__image')) {
      if (image instanceof HTMLImageElement && image.sizes !== sizes) image.sizes = sizes;
    }
  }

  /** Reveals cards as they enter the viewport, left to right, restarting the delay on each row. */
  #observeCards() {
    const pending = this.querySelectorAll('[data-card-reveal="pending"]');
    if (pending.length === 0) return;

    if (reducedMotion.matches) {
      for (const card of pending) if (card instanceof HTMLElement) card.dataset.cardReveal = 'visible';
      return;
    }

    this.#cardObserver ??= new IntersectionObserver(
      (entries) => {
        const cards = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => /** @type {HTMLElement} */ (entry.target))
          .sort((a, b) => a.offsetTop - b.offsetTop || a.offsetLeft - b.offsetLeft);
        if (cards.length === 0) return;

        // On page load the first row waits for the hero entrance
        const base = this.#firstBatch ? 0.9 : 0;
        this.#firstBatch = false;

        let rowTop = -Infinity;
        let column = 0;
        for (const card of cards) {
          if (Math.abs(card.offsetTop - rowTop) > 4) {
            rowTop = card.offsetTop;
            column = 0;
          }
          card.style.setProperty('--collection-lux-card-delay', `${(base + column * 0.1).toFixed(2)}s`);
          card.dataset.cardReveal = 'visible';
          this.#cardObserver?.unobserve(card);
          column += 1;
        }
      },
      { rootMargin: '0px 0px -5% 0px' }
    );

    for (const card of pending) this.#cardObserver.observe(card);
  }
}

/**
 * Color thumbnails on a product card. Clicking one cross-fades the card image to that color and
 * points the card links to the variant (?variant=ID) without navigating.
 */
class LuxCardSwatches extends Component {
  /** @param {Event} event */
  selectColor(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();

    const card = this.closest('.lux-card');
    if (!card) return;

    for (const swatch of this.querySelectorAll('button.lux-card__swatch')) {
      swatch.setAttribute('aria-pressed', String(swatch === button));
    }

    const { variantId, src, srcset } = button.dataset;
    if (variantId) {
      for (const link of card.querySelectorAll('a.lux-card__media-link, a.lux-card__title')) {
        if (!(link instanceof HTMLAnchorElement)) continue;
        const url = new URL(link.href);
        url.searchParams.set('variant', variantId);
        link.href = url.toString();
      }
    }

    // The hover image belongs to the default color, so drop it once a color is chosen
    card.querySelector('.lux-card__image--secondary')?.remove();
    for (const pending of card.querySelectorAll('.lux-card__image--incoming')) pending.remove();

    const current = card.querySelector('img.lux-card__image');
    if (!(current instanceof HTMLImageElement) || !src) return;
    if (current.currentSrc && new URL(current.currentSrc).pathname === new URL(src, location.href).pathname) return;

    const next = /** @type {HTMLImageElement} */ (current.cloneNode());
    next.loading = 'eager';
    next.classList.add('lux-card__image--incoming');
    if (srcset) next.srcset = srcset;
    next.src = src;
    current.after(next);

    const reveal = () => {
      requestAnimationFrame(() => next.classList.add('is-visible'));
      window.setTimeout(() => {
        if (!next.isConnected) return;
        current.remove();
        next.classList.remove('lux-card__image--incoming', 'is-visible');
      }, 500);
    };
    if (next.complete) reveal();
    else next.addEventListener('load', reveal, { once: true });
  }
}

/**
 * @typedef {Object} LuxGalleryRefs
 * @property {HTMLElement} [track] - Scrolling row of photos
 */

/**
 * Edge-to-edge photo slider. Native scroll-snap for touch and trackpads; mouse drag with pointer
 * events (a drag never opens the photo's link); arrows page through the photos.
 *
 * @extends {Component<LuxGalleryRefs>}
 */
class LuxGallery extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  #dragStartX = 0;
  #dragStartScroll = 0;
  #dragged = false;
  #pointerId = -1;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { track } = this.refs;

    if (track) {
      track.addEventListener('scroll', () => updateCarouselArrows(track), { passive: true, signal });
      this.#resizeObserver = new ResizeObserver(() => updateCarouselArrows(track));
      this.#resizeObserver.observe(track);

      track.addEventListener('pointerdown', this.#onPointerDown, { signal });
      track.addEventListener('pointermove', this.#onPointerMove, { signal });
      track.addEventListener('pointerup', this.#onPointerUp, { signal });
      track.addEventListener('pointercancel', this.#onPointerUp, { signal });
      // A drag must not open the photo's link
      track.addEventListener(
        'click',
        (event) => {
          if (!this.#dragged) return;
          event.preventDefault();
          event.stopPropagation();
          this.#dragged = false;
        },
        { capture: true, signal }
      );
    }

    this.#revealObserver = setUpLuxReveal(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#revealObserver?.disconnect();
  }

  /** @param {Event} event */
  scrollPrevious(event) {
    event.preventDefault();
    if (this.refs.track) scrollCarouselPage(this.refs.track, -1);
  }

  /** @param {Event} event */
  scrollNext(event) {
    event.preventDefault();
    if (this.refs.track) scrollCarouselPage(this.refs.track, 1);
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const { track } = this.refs;
    if (!track || event.pointerType !== 'mouse' || event.button !== 0) return;
    this.#pointerId = event.pointerId;
    this.#dragStartX = event.clientX;
    this.#dragStartScroll = track.scrollLeft;
    this.#dragged = false;
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const { track } = this.refs;
    if (!track || event.pointerId !== this.#pointerId) return;
    const delta = event.clientX - this.#dragStartX;
    if (!this.#dragged && Math.abs(delta) < 6) return;

    if (!this.#dragged) {
      this.#dragged = true;
      track.dataset.dragging = '';
      track.setPointerCapture(event.pointerId);
    }
    track.scrollLeft = this.#dragStartScroll - delta;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const { track } = this.refs;
    if (!track || event.pointerId !== this.#pointerId) return;
    this.#pointerId = -1;
    if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    if (!this.#dragged) return;

    // Let snapping settle the row on the nearest photo
    const left = track.scrollLeft;
    delete track.dataset.dragging;
    track.scrollLeft = left;
  };
}

/**
 * @typedef {Object} LuxFaqRefs
 * @property {HTMLAnchorElement[]} [menuLinks] - Side menu links (desktop)
 * @property {HTMLElement[]} [groups] - Category groups
 */

const ACCORDION_DURATION = 350;
const ACCORDION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Opens an accordion row (snippets/lux-accordion-item.liquid) with a height animation.
 * @param {HTMLDetailsElement} details
 */
function openAccordionItem(details) {
  const panel = details.querySelector('.lux-accordion__panel');
  details.classList.remove('is-closing');
  details.open = true;
  if (!(panel instanceof HTMLElement) || reducedMotion.matches) return;
  for (const animation of panel.getAnimations()) animation.cancel();
  panel.animate([{ height: '0px' }, { height: `${panel.scrollHeight}px` }], {
    duration: ACCORDION_DURATION,
    easing: ACCORDION_EASING,
  });
}

/**
 * Closes an accordion row with a height animation.
 * @param {HTMLDetailsElement} details
 */
function closeAccordionItem(details) {
  const panel = details.querySelector('.lux-accordion__panel');
  if (!(panel instanceof HTMLElement) || reducedMotion.matches) {
    details.open = false;
    return;
  }
  for (const animation of panel.getAnimations()) animation.cancel();
  details.classList.add('is-closing');
  const animation = panel.animate([{ height: `${panel.offsetHeight}px` }, { height: '0px' }], {
    duration: ACCORDION_DURATION,
    easing: ACCORDION_EASING,
    fill: 'forwards',
  });
  animation.onfinish = () => {
    details.open = false;
    details.classList.remove('is-closing');
    animation.cancel();
  };
}

/**
 * Toggles the accordion row that contains a clicked summary.
 * @param {Event} event
 * @returns {HTMLDetailsElement | null} The row being opened, or null when it closes or is not a row
 */
function toggleAccordionItem(event) {
  const summary = event.target;
  const details = summary instanceof HTMLElement ? summary.closest('details.lux-accordion__item') : null;
  if (!(details instanceof HTMLDetailsElement)) return null;
  event.preventDefault();

  if (details.open && !details.classList.contains('is-closing')) {
    closeAccordionItem(details);
    return null;
  }
  openAccordionItem(details);
  return details;
}

/**
 * Product information rows ("Acordeón" blocks inside the "Información" block).
 */
class LuxAccordion extends Component {
  /** @param {Event} event */
  toggle(event) {
    toggleAccordionItem(event);
  }
}

/**
 * FAQ accordion on <details>/<summary> (shared rows and animation), optionally keeping only one
 * question open, and highlights the category on screen in the sticky side menu.
 *
 * @extends {Component<LuxFaqRefs>}
 */
class LuxFaq extends Component {
  /** @type {IntersectionObserver | null} */
  #categoryObserver = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  /** @type {Set<Element>} */
  #visibleGroups = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#revealObserver = setUpLuxReveal(this);

    const { groups = [], menuLinks = [] } = this.refs;
    if (groups.length === 0 || menuLinks.length === 0) return;

    this.#categoryObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#visibleGroups.add(entry.target);
          else this.#visibleGroups.delete(entry.target);
        }
        const current = (this.refs.groups ?? []).find((group) => this.#visibleGroups.has(group));
        if (current) this.#markCategory(current.id);
      },
      { rootMargin: '-20% 0px -55% 0px' }
    );
    for (const group of groups) this.#categoryObserver.observe(group);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#categoryObserver?.disconnect();
    this.#revealObserver?.disconnect();
  }

  /** @param {Event} event */
  toggle(event) {
    const opened = toggleAccordionItem(event);
    if (!opened || !this.hasAttribute('data-single-open')) return;
    for (const other of this.querySelectorAll('details.lux-accordion__item[open]')) {
      if (other !== opened && other instanceof HTMLDetailsElement) closeAccordionItem(other);
    }
  }

  /** @param {Event} event */
  scrollToCategory(event) {
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement)) return;
    const target = document.getElementById(link.hash.slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    this.#markCategory(target.id);
  }

  /** @param {string} id */
  #markCategory(id) {
    for (const link of this.refs.menuLinks ?? []) {
      if (link.hash.slice(1) === id) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
  }
}

if (!customElements.get('lux-tabs')) {
  customElements.define('lux-tabs', LuxTabs);
}

if (!customElements.get('lux-reveal')) {
  customElements.define('lux-reveal', LuxReveal);
}

if (!customElements.get('lux-hero')) {
  customElements.define('lux-hero', LuxHero);
}

/**
 * @typedef {Object} LuxVideoRefs
 * @property {HTMLElement} [player] - Where the video is inserted on play
 * @property {HTMLTemplateElement} [template] - The <video> or <iframe> markup, inert until play
 */

/**
 * Full-width video with a cover. The video markup lives in a <template> and is only inserted
 * on play, so nothing is downloaded before the visitor asks for it. When the video ends the
 * cover returns (native video "ended", or the YouTube/Vimeo postMessage APIs).
 *
 * @extends {Component<LuxVideoRefs>}
 */
class LuxVideo extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    window.addEventListener('message', this.#handleMessage, { signal: this.#abortController.signal });
    this.#revealObserver = setUpLuxReveal(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#revealObserver?.disconnect();
  }

  play() {
    const { player, template } = this.refs;
    if (!player || !template) return;

    if (!player.firstElementChild) {
      player.append(template.content.cloneNode(true));
      const media = player.firstElementChild;

      if (media instanceof HTMLVideoElement) {
        media.addEventListener('ended', () => this.#showCover(), { signal: this.#abortController?.signal });
      } else if (media instanceof HTMLIFrameElement && media.dataset.src) {
        media.addEventListener('load', () => this.#listenForEnd(media), { once: true });
        media.src = media.dataset.src;
      }
    }

    this.dataset.playing = '';
    const media = player.firstElementChild;
    if (media instanceof HTMLVideoElement) {
      media.currentTime = 0;
      media.play().catch(() => {});
    }
    if (media instanceof HTMLElement) media.focus({ preventScroll: true });
  }

  /** @param {HTMLIFrameElement} iframe */
  #listenForEnd(iframe) {
    const target = iframe.contentWindow;
    if (!target) return;
    if (iframe.dataset.provider === 'youtube') {
      target.postMessage(JSON.stringify({ event: 'listening', id: this.id || 'lux-video', channel: 'widget' }), '*');
    } else if (iframe.dataset.provider === 'vimeo') {
      target.postMessage(JSON.stringify({ method: 'addEventListener', value: 'ended' }), '*');
    }
  }

  /** @param {MessageEvent} event */
  #handleMessage = (event) => {
    const iframe = this.refs.player?.querySelector('iframe');
    if (!iframe || event.source !== iframe.contentWindow) return;

    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }

    const youtubeEnded = data?.event === 'onStateChange' && data?.info === 0;
    const youtubeState = data?.event === 'infoDelivery' && data?.info?.playerState === 0;
    const vimeoEnded = data?.event === 'ended';
    if (youtubeEnded || youtubeState || vimeoEnded) this.#showCover();
  };

  #showCover() {
    delete this.dataset.playing;
    const media = this.refs.player?.firstElementChild;
    if (media instanceof HTMLVideoElement) {
      media.pause();
    } else if (media) {
      // Remove embeds so they stop playing and free memory; they are recreated on the next play
      window.setTimeout(() => this.refs.player?.replaceChildren(), 600);
    }
  }
}

/**
 * Converts a TikTok, Instagram or YouTube link into an embeddable URL.
 * @param {string} url
 * @returns {string}
 */
function getEmbedUrl(url) {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname;

    if (host.includes('tiktok.com')) {
      const match = parsed.pathname.match(/\/video\/(\d+)/);
      if (match) return `https://www.tiktok.com/embed/v2/${match[1]}`;
    }

    if (host.includes('instagram.com')) {
      return `https://www.instagram.com${parsed.pathname.replace(/\/$/, '')}/embed/`;
    }

    if (host.includes('youtube.com') || host.includes('youtu.be')) {
      const id =
        parsed.searchParams.get('v') ||
        parsed.pathname
          .split('/')
          .filter(Boolean)
          .pop();
      if (id) return `https://www.youtube.com/embed/${id}?autoplay=1&playsinline=1&rel=0`;
    }
  } catch {
    // Not a valid URL: fall through and use it as is
  }
  return url;
}

/**
 * @typedef {Object} LuxDeliveriesRefs
 * @property {HTMLElement} [track] - Carousel track
 * @property {HTMLDialogElement} [modal] - Video dialog
 * @property {HTMLElement} [player] - Where the embed iframe is created
 */

/**
 * Social proof carousel with a video modal. The embed iframe is created when the modal opens
 * and removed when it closes, so nothing third-party loads until a card is clicked.
 *
 * @extends {Component<LuxDeliveriesRefs>}
 */
class LuxDeliveries extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  /** @type {HTMLElement | null} */
  #opener = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { track, modal } = this.refs;

    if (track) {
      track.addEventListener('scroll', () => updateCarouselArrows(track), { passive: true, signal });
      this.#resizeObserver = new ResizeObserver(() => updateCarouselArrows(track));
      this.#resizeObserver.observe(track);
    }

    // Escape closes the native dialog; clean up the iframe and scroll lock however it closes
    modal?.addEventListener('close', () => this.#cleanUpModal(), { signal });

    this.#revealObserver = setUpLuxReveal(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#revealObserver?.disconnect();
    if (this.refs.modal?.open) this.refs.modal.close();
  }

  /** @param {Event} event */
  scrollPrevious(event) {
    event.preventDefault();
    if (this.refs.track) scrollCarouselPage(this.refs.track, -1);
  }

  /** @param {Event} event */
  scrollNext(event) {
    event.preventDefault();
    if (this.refs.track) scrollCarouselPage(this.refs.track, 1);
  }

  /** @param {Event} event */
  openVideo(event) {
    const button = event.target;
    const { modal, player } = this.refs;
    if (!(button instanceof HTMLElement) || !modal || !player) return;

    const url = button.dataset.videoUrl;
    if (!url) return;

    const iframe = document.createElement('iframe');
    iframe.src = getEmbedUrl(url);
    iframe.title = button.getAttribute('aria-label') || '';
    iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    player.replaceChildren(iframe);

    this.#opener = button;
    modal.showModal();
    lockScroll(modal);
  }

  closeVideo() {
    this.refs.modal?.close();
  }

  /**
   * Closes the modal when the click lands outside the video box.
   * @param {MouseEvent} event
   */
  handleModalClick(event) {
    const box = this.refs.modal?.querySelector('.deliveries-lux__modal-box');
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) this.closeVideo();
  }

  #cleanUpModal() {
    const { modal, player } = this.refs;
    player?.replaceChildren();
    if (modal) unlockScroll(modal);
    this.#opener?.focus({ preventScroll: true });
    this.#opener = null;
  }
}

if (!customElements.get('lux-best-sellers')) {
  customElements.define('lux-best-sellers', LuxBestSellers);
}

if (!customElements.get('lux-video')) {
  customElements.define('lux-video', LuxVideo);
}

if (!customElements.get('lux-deliveries')) {
  customElements.define('lux-deliveries', LuxDeliveries);
}

if (!customElements.get('lux-reviews')) {
  customElements.define('lux-reviews', LuxReviews);
}

if (!customElements.get('lux-benefits')) {
  customElements.define('lux-benefits', LuxBenefits);
}

if (!customElements.get('lux-collection-hero')) {
  customElements.define('lux-collection-hero', LuxCollectionHero);
}

if (!customElements.get('lux-collection')) {
  customElements.define('lux-collection', LuxCollection);
}

if (!customElements.get('lux-card-swatches')) {
  customElements.define('lux-card-swatches', LuxCardSwatches);
}

if (!customElements.get('lux-gallery')) {
  customElements.define('lux-gallery', LuxGallery);
}

if (!customElements.get('lux-faq')) {
  customElements.define('lux-faq', LuxFaq);
}

if (!customElements.get('lux-accordion')) {
  customElements.define('lux-accordion', LuxAccordion);
}
